import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import Modal from '@material-ui/core/Modal';
import Box from '@material-ui/core/Box';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import { makeStyles } from '@material-ui/core/styles';
import * as Utils from '../../commons/utils/Utils';
import Loading from '../ui/Loading.js';
import { TextFieldWithCounter } from '../ui/commons/inputs';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';
import { ITEM_TIPO_MANO_DE_OBRA } from '../../commons/constants/itemsTipos.js';

const presupuestoColStyle = {
    textAlign: 'left',
    paddingLeft: '8px',
    paddingRight: '8px',
    whiteSpace: 'nowrap',
    minWidth: '80px',
};

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '95%',
    maxWidth: '1300px',
    maxHeight: '90vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: '8px',
};

const useStyles = makeStyles(() => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
}));

const ESTADO_APROBADO = 'aprobado';
const ESTADO_RECHAZADO = 'rechazado';
const COLOR_PRIMARIO = '#44c8cf';
const COLOR_PRIMARIO_ACTIVO = '#2fa8ae';
const COLOR_WARNING = '#f0ad4e'
/**
 * Modal para seleccionar tipo de recotización (total o parcial)
 * y, en caso parcial, revisar ítems uno por uno.
 *
 * Props:
 *   open          {boolean}
 *   onClose       {function}
 *   ticket        {object}
 *   intl          {object}
 *   onConfirm     {function}  - llamado tras confirmar exitosamente
 *   ajaxHandler   {object}    - instancia de AjaxHandler del padre (TicketHeader)
 */
const TicketRecotizacionModal = (props) => {
    const classes = useStyles();
    const trabajaConManoDeObra = ConfigBusiness.get('tickets.trabajaConManoDeObra.habilitado') === 'true';
    const [paso, setPaso] = useState(1);
    const [tipo, setTipo] = useState('total');
    const [loading, setLoading] = useState(false);
    const [revisiones, setRevisiones] = useState([]);
    const [presupuestoId, setPresupuestoId] = useState(null);
    const [errores, setErrores] = useState([]);
    const [filtro, setFiltro] = useState('');
    const [pagina, setPagina] = useState(1);
    const POR_PAGINA = 10;
    const [tipoRecotizacion, setTipoRecotizacion] = useState(null);
    const [diffAnterior, setDiffAnterior] = useState(null);
    const [loadingDiff, setLoadingDiff] = useState(false);
    const [mostrarDiff, setMostrarDiff] = useState(false);
    const [diffVisto, setDiffVisto] = useState(false);
    const [tooltipVisible, setTooltipVisible] = useState(false);

    useEffect(() => {
        if (props.open) {
            setPaso(1);
            setTipo('total');
            setRevisiones([]);
            setPresupuestoId(null);
            setErrores([]);
            setFiltro('');
            setPagina(1);
            setTipoRecotizacion(null);
            setDiffAnterior(null);
            setMostrarDiff(false);
            setDiffVisto(false);
        }
    }, [props.open]);

    const cargarItemsParaParcial = () => {
        setLoading(true);
        Utils.getData('/ticket-presupuestos/ticket/' + props.ticket.id + '/items-activos')
            .then((data) => {
                if (!data) {
                    setErrores(['No se encontró un presupuesto activo para este ticket.']);
                    setLoading(false);
                    return;
                }
                setPresupuestoId(data.presupuestoId);
                setTipoRecotizacion(data.tipoRecotizacion || null);
                const items = (data.items || []).map((item) => ({
                    id: item.id,
                    idItem: item.id_item,
                    servicioId: item.servicio_id || null,
                    servicioNombre: item.servicio && item.servicio.nombre ? item.servicio.nombre : (item.servicio_nombre || null),
                    tipoItem: 'item',
                    itemTipo: item.item && item.item.tipo ? item.item.tipo : null,
                    descripcion: item.descripcion,
                    precio: item.precio,
                    cantidad: item.cantidad,
                    estadoRecotizacion: ESTADO_APROBADO,
                    comentarioRechazo: '',
                }));
                const tareas = (data.tareas || []).map((tarea) => ({
                    id: tarea.id,
                    idTarea: tarea.id_tarea,
                    servicioId: tarea.servicio_id || null,
                    servicioNombre: tarea.servicio && tarea.servicio.nombre ? tarea.servicio.nombre : (tarea.servicio_nombre || null),
                    tipoItem: 'tarea',
                    descripcion: tarea.descripcion,
                    precio: tarea.precio,
                    cantidad: tarea.cantidad || 1,
                    estadoRecotizacion: ESTADO_APROBADO,
                    comentarioRechazo: '',
                }));
                setRevisiones([...items, ...tareas]);
                setPagina(1);
                setLoading(false);
            })
            .catch(() => {
                setErrores(['Error al cargar los ítems del presupuesto.']);
                setLoading(false);
            });
    };

    const handleToggleRechazar = (index) => {
        const copia = revisiones.slice();
        const actual = copia[index].estadoRecotizacion;
        const nuevoEstado = actual === ESTADO_RECHAZADO ? ESTADO_APROBADO : ESTADO_RECHAZADO;
        copia[index] = Object.assign({}, copia[index], {
            estadoRecotizacion: nuevoEstado,
            comentarioRechazo: nuevoEstado === ESTADO_APROBADO ? '' : copia[index].comentarioRechazo,
        });
        setRevisiones(copia);
    };

    const handleComentario = (index, valor) => {
        const copia = revisiones.slice();
        copia[index] = Object.assign({}, copia[index], { comentarioRechazo: valor });
        setRevisiones(copia);
    };

    const puedeConfirmar = () => {
        if (tipo === 'total') return true;
        if (rechazados.length === 0) return false;
        return rechazados.every((r) => r.comentarioRechazo && r.comentarioRechazo.trim().length > 0);
    };

    const handleConfirmar = () => {
        setErrores([]);
        setLoading(true);

        const body = {
            tipo: tipo,
            revisiones: tipo === 'parcial'
                ? revisiones.map((r) => ({
                    id: r.id,
                    tipoItem: r.tipoItem,
                    estadoRecotizacion: r.estadoRecotizacion,
                    comentarioRechazo: r.estadoRecotizacion === ESTADO_RECHAZADO ? r.comentarioRechazo : null,
                }))
                : [],
        };

        props.ajaxHandler
            .fetch((props.endpoint || '/tickets/solicitar-recotizacion/') + props.ticket.id, {
                method: 'POST',
                body: JSON.stringify(body),
            })
            .then((response) => {
                setLoading(false);
                if (response.status === 204) {
                    props.onClose();
                    props.onConfirm();
                } else {
                    response.json().then((data) => {
                        setErrores(data.detalle || ['Error al procesar la recotización.']);
                    });
                }
            })
            .catch(() => {
                setLoading(false);
                setErrores(['Error de conexión al procesar la recotización.']);
            });
    };

    const handleVerDiff = () => {
        if (diffAnterior !== null) {
            setMostrarDiff(!mostrarDiff);
            return;
        }
        setLoadingDiff(true);
        Utils.getData('/ticket-presupuestos/ticket/' + props.ticket.id + '/precargar-items')
            .then((data) => {
                setDiffAnterior(data || { items: [], tareas: [] });
                setMostrarDiff(true);
                setLoadingDiff(false);
            })
            .catch(() => {
                setLoadingDiff(false);
            });
    };


    // Devuelve true si el item debe tratarse como mano de obra, y false en caso contrario.
    // Para tareas ('tarea') siempre retorna true (flujo legacy).
    var esItemManoDeObra = function(revision) {
        if (revision.tipoItem === 'tarea') return true;
        if (trabajaConManoDeObra && revision.itemTipo && revision.itemTipo.toLowerCase() === ITEM_TIPO_MANO_DE_OBRA.toLowerCase()) return true;
        return false;
    };

    // Igual pero para filas eliminadas del diff (estructura distinta)
    var esEliminadoManoDeObra = function(el) {
        if (el._tipoItem === 'tarea') return true;
        if (trabajaConManoDeObra && el.item && el.item.tipo && el.item.tipo.toLowerCase() === ITEM_TIPO_MANO_DE_OBRA.toLowerCase()) return true;
        return false;
    };

    const rechazados = revisiones.filter((r) => r.estadoRecotizacion === ESTADO_RECHAZADO);

    const handleSeleccionarTipo = (tipoSeleccionado) => {
        setTipo(tipoSeleccionado);
        if (tipoSeleccionado === 'parcial') {
            cargarItemsParaParcial();
        }
        setPaso(2);
    };

    const renderPaso1 = () => (
        <div>
            <h2 className="text-center mb-2" style={{ color: COLOR_PRIMARIO }}>
                <b><FormattedMessage id="recotizacion.modal.titulo" defaultMessage="Tipo de Recotización" /></b>
            </h2>
            <p className="text-center text-muted mb-3">
                <FormattedMessage
                    id="recotizacion.modal.descripcion"
                    defaultMessage="Seleccioná cómo querés recotizar este presupuesto."
                />
            </p>
            <div className="d-flex justify-content-center">
                <div className="d-flex flex-column" style={{ gap: '12px', marginBottom: '20px', width: '200px' }}>
                    <button type="button" className="btn btn-primary" onClick={() => handleSeleccionarTipo('total')}>
                        <FormattedMessage id="recotizacion.tipo.total" defaultMessage="Recotización total" />
                    </button>
                    <button type="button" className="btn btn-primary" onClick={() => handleSeleccionarTipo('parcial')}>
                        <FormattedMessage id="recotizacion.tipo.parcial" defaultMessage="Recotización parcial" />
                    </button>
                </div>
            </div>
            <div className="text-center">
                <button
                    type="button"
                    className="btn btn-danger"
                    onClick={props.onClose}
                >
                    <i className="fa fa-times-circle"></i>&nbsp;<FormattedMessage id="recotizacion.modal.cancelar" defaultMessage="Cancelar" />
                </button>
            </div>
        </div>
    );

    const renderPaso2Total = () => (
        <div className="text-center">
            <h2 className="text-center mb-2" style={{ color: COLOR_PRIMARIO }}>
                <b><FormattedMessage id="recotizacion.total.titulo" defaultMessage="Confirmación de recotización total" /></b>
            </h2>
            <p>
                <FormattedMessage
                    id="recotizacion.total.confirmacion"
                    defaultMessage="Se solicitará una recotización total. El proveedor podrá editar el presupuesto completo."
                />
            </p>
            <div className="mt-3">
                <button
                    type="button"
                    className="btn btn-primary mr-1"
                    onClick={handleConfirmar}
                    disabled={loading}
                >
                    <i className="fa fa-check-circle"></i>&nbsp;
                    {loading
                        ? <FormattedMessage id="recotizacion.modal.procesando" defaultMessage="Procesando..." />
                        : <FormattedMessage id="recotizacion.modal.confirmar" defaultMessage="Confirmar recotización total" />
                    }
                </button>
                <button type="button" className="btn btn-danger" onClick={() => setPaso(1)} disabled={loading}>
                    <i className="fa fa-times-circle"></i>&nbsp;
                    <FormattedMessage id="recotizacion.modal.volver" defaultMessage="Volver" />
                </button>
            </div>
        </div>
    );

    const renderPaso2Parcial = () => {
        // Compute diff per revision when diff mode is active
        var diffPorRevision = {};
        var todosEliminados = [];
        if (mostrarDiff && diffAnterior) {
            var anteriorItemsDiff = diffAnterior.items || [];
            var anteriorTareasDiff = diffAnterior.tareas || [];
            revisiones.forEach(function(rev) {
                var key = rev.tipoItem + '-' + rev.id;
                var ant = null;
                if (rev.tipoItem === 'item') {
                    ant = anteriorItemsDiff.find(function(a) {
                        return rev.idItem
                            ? a.id_item === rev.idItem && a.servicio_id === rev.servicioId
                            : a.descripcion === rev.descripcion && a.servicio_id === rev.servicioId;
                    });
                } else {
                    ant = anteriorTareasDiff.find(function(a) {
                        return rev.idTarea
                            ? a.id_tarea === rev.idTarea && a.servicio_id === rev.servicioId
                            : a.descripcion === rev.descripcion && a.servicio_id === rev.servicioId;
                    });
                }
                if (!ant) {
                    diffPorRevision[key] = { tipo: 'nuevo', cambios: [] };
                } else {
                    var cambios = [];
                    if (String(ant.precio) !== String(rev.precio)) cambios.push({ campo: 'precio', anterior: ant.precio, nuevo: rev.precio });
                    if (String(ant.cantidad) !== String(rev.cantidad)) cambios.push({ campo: 'cantidad', anterior: ant.cantidad, nuevo: rev.cantidad });
                    if (ant.descripcion !== rev.descripcion) cambios.push({ campo: 'descripcion', anterior: ant.descripcion, nuevo: rev.descripcion });
                    diffPorRevision[key] = cambios.length > 0
                        ? { tipo: 'modificado', cambios: cambios, estadoAnterior: ant.estado_recotizacion }
                        : { tipo: 'sin_cambios', cambios: [], estadoAnterior: ant.estado_recotizacion };
                }
            });
            anteriorItemsDiff.forEach(function(a) {
                var existe = revisiones.find(function(r) {
                    return r.tipoItem === 'item' && (r.idItem
                        ? r.idItem === a.id_item && r.servicioId === a.servicio_id
                        : r.descripcion === a.descripcion && r.servicioId === a.servicio_id);
                });
                if (!existe) todosEliminados.push(Object.assign({}, a, { _tipoItem: 'item', _servicioNombre: a.servicio && a.servicio.nombre ? a.servicio.nombre : (a.servicio_nombre || null) }));
            });
            anteriorTareasDiff.forEach(function(a) {
                var existe = revisiones.find(function(r) {
                    return r.tipoItem === 'tarea' && (r.idTarea
                        ? r.idTarea === a.id_tarea && r.servicioId === a.servicio_id
                        : r.descripcion === a.descripcion && r.servicioId === a.servicio_id);
                });
                if (!existe) todosEliminados.push(Object.assign({}, a, { _tipoItem: 'tarea', _servicioNombre: a.servicio && a.servicio.nombre ? a.servicio.nombre : (a.servicio_nombre || null) }));
            });
        }

        var filtroLower = filtro.toLowerCase();
        var revisionesFiltradas = revisiones.reduce(function(acc, revision, realIndex) {
            var matchTipo = esItemManoDeObra(revision) ? 'mano de obra' : 'repuesto';
            var matchDescripcion = revision.descripcion ? revision.descripcion.toLowerCase() : '';
            if (!filtroLower || matchDescripcion.indexOf(filtroLower) !== -1 || matchTipo.indexOf(filtroLower) !== -1) {
                acc.push({ revision: revision, realIndex: realIndex });
            }
            return acc;
        }, []);

        var eliminadosFiltrados = mostrarDiff ? todosEliminados.filter(function(a) {
            if (!filtroLower) return true;
            var matchTipo = esEliminadoManoDeObra(a) ? 'mano de obra' : 'repuesto';
            var matchDesc = a.descripcion ? a.descripcion.toLowerCase() : '';
            return matchDesc.indexOf(filtroLower) !== -1 || matchTipo.indexOf(filtroLower) !== -1;
        }) : [];

        var filasCombinadas = mostrarDiff
            ? revisionesFiltradas.concat(eliminadosFiltrados.map(function(a) { return { eliminado: a }; }))
            : revisionesFiltradas;
        var totalPaginas = Math.max(1, Math.ceil(filasCombinadas.length / POR_PAGINA));
        var paginaActual = Math.min(pagina, totalPaginas);
        var inicio = (paginaActual - 1) * POR_PAGINA;
        var filasPagina = filasCombinadas.slice(inicio, inicio + POR_PAGINA);

        var DIFF_COLORES = { nuevo: '#d4edda', modificado: '#fff3cd', eliminado: '#f8d7da', sin_cambios: 'transparent' };
        var DIFF_BADGE = { nuevo: 'badge badge-success', modificado: 'badge badge-warning', eliminado: 'badge badge-danger', sin_cambios: 'badge badge-secondary' };
        var DIFF_LABEL = { nuevo: 'Nuevo', modificado: 'Modificado', eliminado: 'Eliminado', sin_cambios: 'Sin cambios' };
        var totalCols = mostrarDiff ? 6 : 7;

        return (
            <div>
                <h2 className="pt-2 pb-2" style={{color: COLOR_PRIMARIO}}><b><FormattedMessage id="recotizacion.parcial.titulo" defaultMessage="Revisión por ítem"/></b></h2>

                <p className="text-muted" style={{ fontSize: '13px' }}>
                    <FormattedMessage
                        id="recotizacion.parcial.instruccion"
                        defaultMessage="Marcá los ítems que querés rechazar. Los no marcados quedan aprobados automáticamente."
                    />
                </p>

                <div className="d-flex align-items-center justify-content-between mb-2">
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={props.intl.formatMessage({ id: 'recotizacion.filtro.placeholder', defaultMessage: 'Filtrar por descripción o tipo...' })}
                        value={filtro}
                        onChange={(e) => { setFiltro(e.target.value); setPagina(1); }}
                        style={{ maxWidth: '320px' }}
                    />
                    {tipoRecotizacion && (
                        <span
                            style={{ position: 'relative', display: 'inline-flex' }}
                            onMouseEnter={() => setTooltipVisible(true)}
                            onMouseLeave={() => setTooltipVisible(false)}
                        >
                            {tooltipVisible && (
                                <div style={{ position: 'absolute', bottom: 'calc(100% + 6px)', right: 0, background: 'rgba(40,40,40,0.92)', color: '#fff', padding: '5px 10px', borderRadius: '4px', fontSize: '12px', whiteSpace: 'nowrap', zIndex: 9999, pointerEvents: 'none' }}>
                                    {props.intl.formatMessage({ id: 'recotizacion.diff.ver_historial', defaultMessage: 'Ver cambios respecto al presupuesto recotizado' })}
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={() => { setDiffVisto(true); handleVerDiff(); }}
                                disabled={loadingDiff}
                                className={!diffVisto ? 'btn-history-pulse' : ''}
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px', borderRadius: '50%', backgroundColor: !diffVisto ? COLOR_WARNING : (mostrarDiff ? COLOR_PRIMARIO_ACTIVO : COLOR_PRIMARIO), border: 'none', cursor: 'pointer', padding: 0 }}
                            >
                                {loadingDiff
                                    ? <i className="la la-spinner la-spin" style={{ fontSize: '18px', color: '#fff' }}></i>
                                    : <i className="la la-history" style={{ fontSize: '20px', color: '#fff' }}></i>
                                }
                            </button>
                        </span>
                    )}
                </div>

                <div className="table-responsive">
                    <table className="table">
                        <thead className="thead-fleet">
                            <tr>
                                {mostrarDiff && (
                                    <th scope="col" style={presupuestoColStyle}><FormattedMessage id="recotizacion.diff.estado" defaultMessage="Estado" /></th>
                                )}
                                <th scope="col" style={presupuestoColStyle}><FormattedMessage id="recotizacion.tabla.tipo" defaultMessage="Tipo" /></th>
                                <th scope="col" style={presupuestoColStyle}><FormattedMessage id="recotizacion.tabla.descripcion" defaultMessage="Descripción" /></th>
                                {!mostrarDiff && (
                                    <th scope="col" style={Object.assign({}, presupuestoColStyle, { textAlign: 'right' })}>
                                        <FormattedMessage id="recotizacion.tabla.cantidad" defaultMessage="Cantidad" />
                                    </th>
                                )}
                                {!mostrarDiff && (
                                    <th scope="col" style={Object.assign({}, presupuestoColStyle, { textAlign: 'right' })}>
                                        <FormattedMessage id="recotizacion.tabla.precio_unitario" defaultMessage="Precio unitario" />
                                    </th>
                                )}
                                {!mostrarDiff && (
                                    <th scope="col" style={Object.assign({}, presupuestoColStyle, { textAlign: 'right' })}>
                                        <FormattedMessage id="recotizacion.tabla.total" defaultMessage="Total" />
                                    </th>
                                )}
                                {mostrarDiff && (
                                    <th scope="col" style={presupuestoColStyle}><FormattedMessage id="recotizacion.diff.detalle" defaultMessage="Detalle del cambio" /></th>
                                )}
                                <th scope="col" style={Object.assign({}, presupuestoColStyle, { textAlign: 'center' })}>
                                    <FormattedMessage id="recotizacion.tabla.accion" defaultMessage="Acción" />
                                </th>
                                <th scope="col" style={presupuestoColStyle}><FormattedMessage id="recotizacion.tabla.comentario" defaultMessage="Comentario de rechazo" /></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filasPagina.length === 0 ? (
                                <tr>
                                    <td colSpan={totalCols} className="text-center text-muted" style={{ padding: '16px' }}>
                                        <FormattedMessage id="recotizacion.filtro.sin_resultados" defaultMessage="No hay ítems que coincidan con el filtro." />
                                    </td>
                                </tr>
                            ) : filasPagina.map(function(fila, idx) {
                                if (fila.eliminado) {
                                    var el = fila.eliminado;
                                    return (
                                        <tr key={'eliminado-' + idx} style={{ backgroundColor: DIFF_COLORES.eliminado }}>
                                            <td><span className={DIFF_BADGE.eliminado}>{DIFF_LABEL.eliminado}</span></td>
                                            <td>{esEliminadoManoDeObra(el)
                                                ? <FormattedMessage id="recotizacion.tipo_item.tarea" defaultMessage="Mano de Obra" />
                                                : <FormattedMessage id="recotizacion.tipo_item.repuesto" defaultMessage="Repuesto" />
                                            }</td>
                                            <td>
                                                {el.descripcion}
                                                {el._servicioNombre && (
                                                    <span className="text-muted"> ({el._servicioNombre})</span>
                                                )}
                                            </td>
                                            <td><span className="text-danger" style={{ fontSize: '12px' }}>No está en el presupuesto actual</span></td>
                                            <td></td>
                                            <td></td>
                                        </tr>
                                    );
                                }

                                var revision = fila.revision;
                                var realIndex = fila.realIndex;
                                var diffKey = revision.tipoItem + '-' + revision.id;
                                var diffEntry = diffPorRevision[diffKey] || null;
                                var esModoAprobadoModificado = mostrarDiff && diffEntry && diffEntry.tipo === 'modificado' && diffEntry.estadoAnterior === 'aprobado';
                                var rowBg = mostrarDiff && diffEntry
                                    ? (esModoAprobadoModificado ? DIFF_COLORES.eliminado : DIFF_COLORES[diffEntry.tipo])
                                    : (revision.estadoRecotizacion === ESTADO_RECHAZADO ? '#fff5f5' : 'transparent');

                                return (
                                    <tr key={revision.tipoItem + '-' + revision.id} style={{ backgroundColor: rowBg }}>
                                        {mostrarDiff && (
                                            <td>
                                                {diffEntry && (
                                                    <span className={esModoAprobadoModificado ? DIFF_BADGE.eliminado : DIFF_BADGE[diffEntry.tipo]}>
                                                        {DIFF_LABEL[diffEntry.tipo]}
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                        <td>
                                            {esItemManoDeObra(revision)
                                                ? <FormattedMessage id="recotizacion.tipo_item.tarea" defaultMessage="Mano de Obra" />
                                                : <FormattedMessage id="recotizacion.tipo_item.repuesto" defaultMessage="Repuesto" />
                                            }
                                        </td>
                                        <td>
                                            {revision.descripcion}
                                            {revision.servicioNombre && (
                                                <span className="text-muted"> ({revision.servicioNombre})</span>
                                            )}
                                        </td>
                                        {!mostrarDiff && (
                                            <td style={{ textAlign: 'right' }}>
                                                {revision.cantidad != null ? revision.cantidad : '-'}
                                            </td>
                                        )}
                                        {!mostrarDiff && (
                                            <td style={{ textAlign: 'right' }}>
                                                {revision.precio != null ? '$ ' + parseFloat(revision.precio).toFixed(2) : '-'}
                                            </td>
                                        )}
                                        {!mostrarDiff && (
                                            <td style={{ textAlign: 'right' }}>
                                                {revision.precio != null && revision.cantidad != null
                                                    ? '$ ' + parseFloat(revision.precio * revision.cantidad).toFixed(2)
                                                    : '-'}
                                            </td>
                                        )}
                                        {mostrarDiff && (
                                            <td style={{ fontSize: '12px' }}>
                                                {diffEntry && diffEntry.tipo === 'nuevo' && (
                                                    <span className="text-success">Ítem agregado al presupuesto actual</span>
                                                )}
                                                {diffEntry && diffEntry.tipo === 'sin_cambios' && (
                                                    <span className="text-muted">Sin cambios respecto al rechazado</span>
                                                )}
                                                {diffEntry && diffEntry.tipo === 'modificado' && diffEntry.cambios.map(function(c, ci) {
                                                    return (
                                                        <div key={ci}>
                                                            <b>{c.campo}:</b>{' '}
                                                            <span className="text-danger">{c.anterior != null ? c.anterior : '—'}</span>
                                                            {' → '}
                                                            <span className="text-success">{c.nuevo != null ? c.nuevo : '—'}</span>
                                                        </div>
                                                    );
                                                })}
                                            </td>
                                        )}
                                        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            <button
                                                type="button"
                                                className={'btn btn-sm ' + (revision.estadoRecotizacion === ESTADO_RECHAZADO ? 'btn-danger' : 'btn-outline-danger')}
                                                onClick={() => handleToggleRechazar(realIndex)}
                                                title={props.intl.formatMessage({ id: 'recotizacion.rechazar', defaultMessage: 'Rechazar' })}
                                            >
                                                <i className="ft-thumbs-down"></i>
                                                &nbsp;<FormattedMessage id="recotizacion.rechazar" defaultMessage="Rechazar" />
                                            </button>
                                        </td>
                                        <td>
                                            <div style={{ visibility: revision.estadoRecotizacion === ESTADO_RECHAZADO ? 'visible' : 'hidden' }}>
                                                <TextFieldWithCounter
                                                    maxLength={60}
                                                    placeholder={props.intl.formatMessage({
                                                        id: 'recotizacion.comentario.placeholder',
                                                        defaultMessage: 'Motivo del rechazo*',
                                                    })}
                                                    value={revision.comentarioRechazo}
                                                    onChange={(e) => handleComentario(realIndex, e.target.value)}
                                                    fullWidth
                                                    error={!revision.comentarioRechazo || revision.comentarioRechazo.trim().length === 0}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {totalPaginas > 1 && (
                    <div className="d-flex align-items-center justify-content-between mt-2 mb-1" style={{ fontSize: '13px' }}>
                        <span className="text-muted">
                            <FormattedMessage
                                id="recotizacion.paginacion.info"
                                defaultMessage="Página {pagina} de {total}"
                                values={{ pagina: paginaActual, total: totalPaginas }}
                            />
                            {' '}({filasCombinadas.length} <FormattedMessage id="recotizacion.paginacion.items" defaultMessage="ítems" />)
                        </span>
                        <div>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary mr-1"
                                onClick={() => setPagina(paginaActual - 1)}
                                disabled={paginaActual <= 1}
                            >
                                <i className="fa fa-chevron-left"></i>
                                &nbsp;<FormattedMessage id="recotizacion.paginacion.anterior" defaultMessage="Anterior" />
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setPagina(paginaActual + 1)}
                                disabled={paginaActual >= totalPaginas}
                            >
                                <FormattedMessage id="recotizacion.paginacion.siguiente" defaultMessage="Siguiente" />
                                &nbsp;<i className="fa fa-chevron-right"></i>
                            </button>
                        </div>
                    </div>
                )}

                <div className="text-center mt-2">
                    <span className="text-danger d-block mb-2" style={{ fontSize: '12px', visibility: !puedeConfirmar() ? 'visible' : 'hidden' }}>
                        {rechazados.length === 0
                            ? <FormattedMessage id="recotizacion.parcial.error_sin_rechazados" defaultMessage="Debés rechazar al menos un ítem para continuar." />
                            : <FormattedMessage id="recotizacion.parcial.error_comentario" defaultMessage="Completá el comentario de todos los ítems rechazados." />
                        }
                    </span>
                    <button
                        type="button"
                        className="btn btn-primary mr-1"
                        onClick={handleConfirmar}
                        disabled={!puedeConfirmar() || loading}
                    >
                        <i className="fa fa-check-circle"></i>&nbsp;
                        {loading
                            ? <FormattedMessage id="recotizacion.modal.procesando" defaultMessage="Procesando..." />
                            : <FormattedMessage id="recotizacion.parcial.confirmar" defaultMessage="Confirmar recotización parcial" />
                        }
                    </button>
                    <button type="button" className="btn btn-danger" onClick={() => setPaso(1)} disabled={loading}>
                        <i className="fa fa-times-circle"></i>
                        &nbsp;<FormattedMessage id="recotizacion.modal.volver" defaultMessage="Volver" />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <Modal
            open={props.open}
            onClose={props.onClose}
            className={classes.modal}
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Fade in={props.open}>
                <Box sx={(paso === 1 || (paso === 2 && tipo === 'total')) ? Object.assign({}, modalStyle, { maxWidth: '500px' }) : modalStyle}>
                    {errores.length > 0 && (
                        <div className="alert alert-danger">
                            {errores.map((e, i) => <div key={i}>{e}</div>)}
                        </div>
                    )}
                    {loading && paso === 2 && tipo === 'parcial' && revisiones.length === 0
                        ? <Loading />
                        : paso === 1
                            ? renderPaso1()
                            : tipo === 'total'
                                ? renderPaso2Total()
                                : renderPaso2Parcial()
                    }
                </Box>
            </Fade>
        </Modal>
    );
};

export default TicketRecotizacionModal;
